import {
  ChatSessionSubCodeAction,
  ESessionType,
  MIND_MODE,
  SENDER_TYPE,
  SessionErrorCode,
  SessionMessageType,
  WS_MSG_DATA_TYPE
} from '@mindverse/accessor-open';
import type {
  ErrorMsg,
  HistoryMessageModel,
  IQuote,
  SessionMessageModel,
  SocketSessionManager,
  WS_MIND_TYPE
} from '@mindverse/accessor-open';
import { useMemoizedFn } from 'ahooks';
import { message } from 'antd';
import { throttle } from 'lodash';
import { useEffect, useRef, useState } from 'react';

import { modal } from '@/components/CommonModal/commonModal';
import { useModel } from '@/hooks/useModel';
import { useTranslation } from '@/i18n/client';
import { EGlobalLocalStorageKey } from '@/types/common';
import { useHomepageContext } from '@/ui/HomePage/HomepageContext';
import { EVENT } from '@/utils/event';
import reportOther, { EReportOtherKey } from '@/utils/report/reportOther';

import type MessageItem from '../components/chat/model/message-item';
import { MessageItemType } from '../components/chat/model/message-item';
import updateSummary from '../services/session';
import type { MindConfig } from '../types/common';
import updateURLSearch from '../utils/updateURLSearch';
import type { EventCreateSessionInterface, SessionSourceInterface } from '../utils/utils';
import { CONTAINER_EVENT, INNER_EVENT, getGMTNumber, showToast } from '../utils/utils';
import useAudio from './useAudio';
import useMindStorage, { EStorageKey } from './useMindStorage';
import type { MessageModel } from './useSessionMsg';
import { useSessionMsgList } from './useSessionMsg';

export interface SendMsgData {
  value: string;
  files?: string[];
  quote?: IQuote;
}

export interface SessionMindConfig {
  mindId: string;
  mindType: WS_MIND_TYPE;
  mode?: MIND_MODE;
  sourceMessageId?: string;
}

export enum SessionActionType {
  ERROR = 'error',
  WARNING = 'warning',
  ERROR_WITHOUT_ACTION = 'error_without_action'
}

export interface SessionAction {
  type: SessionActionType;
  content?: string;
}

export interface IProps {
  sessionManager?: SocketSessionManager;
  mindConfig: SessionMindConfig;
  isLogin: boolean;
  disabled?: boolean;
  voiceMute: boolean;
  messageList?: SessionMessageModel[];

  inSharedHistory?: boolean;
  sendMsgCb?: () => void;
}

export const MV_SESSION_ERROR_CODE = {
  NO_ENOUGH_KEY: 'notEnoughCredit'
};

export interface IResult {
  msgList: MessageModel[];
  noMore?: boolean;
  sessionId: string | undefined;
  createSession: (createProps: ICreateProps) => void;
  doCreateSession: (createProps: ICreateProps) => Promise<any>;
  sendAction: () => void;
  sendMsg: (
    msg: SendMsgData,
    params?: { sessionId?: string | undefined; voiceChannel?: boolean }
  ) => undefined | Promise<boolean>;
  receivedAction?: SessionAction;
  loadingMsgId?: string;
  fetchHistory: () => Promise<HistoryMessageModel>;
  isCreatingSession: boolean;
}

interface IOpenSessionParam {
  cb?: (...args: any[]) => any;
}

export interface ICreateProps extends IOpenSessionParam {
  newSession?: boolean;
  originSessionId?: string;
  extParams?: Record<string, any>;
}

let sendMsgTime: number | undefined;
let receivedMsgStartTime: number | undefined;
let receivedHintMsgStartTime: number | undefined;
let endingMsgTime: number | undefined;

export function useChatSession(props: IProps): IResult {
  const {
    sessionManager,
    mindConfig,
    voiceMute,
    messageList,
    inSharedHistory,
    sendMsgCb,
    disabled = false
  } = props;

  const { mindId } = mindConfig;
  const [loadingMsgId, setLoadingMsgId] = useState<string | undefined>(undefined);
  const [answerStopped] = useMindStorage(EStorageKey.ANSWER_STOPPED);
  const [, setGreetingOnly] = useMindStorage(EStorageKey.GREETING_ONLY);
  const [, setAnswering] = useMindStorage(EStorageKey.ANSWER_ING);
  const [, setMessageId] = useMindStorage(EStorageKey.MESSAGE_ID);
  const [, setIceBreaking] = useMindStorage(EStorageKey.ICE_BREAKING);
  const [, setShowMessageLoading] = useMindStorage(EStorageKey.SHOW_MESSAGE_LOADING);
  const [, setInputShowLogin] = useMindStorage(EStorageKey.INPUT_SHOW_LOGIN);
  const [sessionId, setStorageSession] = useMindStorage(EStorageKey.SESSION_ID);
  const [, setMindId] = useMindStorage(EStorageKey.MIND_ID);
  const [_sendMsgLoading, setSendMsgLoading] = useMindStorage(EStorageKey.SEND_MSG_LOADING);
  const [isCreatingSession, setIsCreatingSession] = useState<boolean>(false);
  const [privateMindConfig, setPrivateMindConfig] = useState<MindConfig>(mindConfig);

  const { homepageData } = useHomepageContext();
  const { userProfile } = useModel('auth-model', (model) => ({
    userProfile: model.userProfile
  }));

  const { t } = useTranslation();

  // history
  const [noMore, setNoMore] = useState<boolean | undefined>(undefined);
  const lastErrorSeqIdRef = useRef<string>();

  const [sessionInfo, setSessionInfo] = useMindStorage(EStorageKey.SESSION_INFO);
  const [_, setConnectedSessionIdList] = useMindStorage(EStorageKey.CONNECTED_SESSION_ID_LIST);

  // 无限流ChatList
  const [, setReady] = useMindStorage(EStorageKey.READY_TO_SET_CHAT_LIST);

  useEffect(() => {
    setPrivateMindConfig((origin) => {
      const { mindId: privateMindId, mindType, mode } = mindConfig;

      if (origin.mindId === privateMindId && origin.mindType === mindType && origin.mode === mode) {
        return origin;
      }

      return {
        ...mindConfig
      };
    });
    setMindId(mindConfig.mindId);
  }, [mindConfig]);

  useEffect(() => {
    if (answerStopped) {
      setLoadingMsgId(undefined);
    }
  }, [answerStopped]);

  // useAudio
  useAudio({ voiceMute });

  const receivedNewMsgCallback = useMemoizedFn((newMsgList: SessionMessageModel[]) => {
    if (disabled) {
      return;
    }

    // 收到消息，说明没有 subCode
    setAction(undefined);

    if (receivedHintMsgStartTime && newMsgList.at(-1)?.type === SessionMessageType.hint) {
      const endTime = Date.now();
      const duration = endTime - receivedHintMsgStartTime;

      reportOther(EReportOtherKey.timer_receive_hint_message, {
        key1: mindId,
        key2: duration,
        key3: localStorage.getItem(EGlobalLocalStorageKey.userId) || '',
        key4: sessionId || '',
        key5: newMsgList.at(-1)?.messageId,
        key6: receivedHintMsgStartTime,
        key7: endTime
      });

      receivedHintMsgStartTime = undefined;
    }

    const serverMsgList = newMsgList.filter((v) => v.sender !== SENDER_TYPE.client);

    if (serverMsgList.length && sessionInfo?.type === ESessionType.MEBOT) {
      setAnswering(true);
    }

    const textMsgList = serverMsgList.filter((v) => v.type === SessionMessageType.msg);

    if (textMsgList.length > 0) {
      setLoadingMsgId(undefined);
      setShowMessageLoading(false);

      const iceBreakEnd = textMsgList.some((v) => {
        const templateData = v.multipleData?.[0].modal.template || '';

        return templateData.includes('MebotChatOver') && templateData.includes('BOT_END');
      });

      if (iceBreakEnd) {
        setIceBreaking(false);
      }
    }

    const lastMsg = newMsgList.at(-1);
    const lastMessageMsg = textMsgList.at(-1);

    if (lastMessageMsg) {
      if (lastMessageMsg.index !== -1) {
        endingMsgTime = Date.now();
      }

      setMessageId(lastMessageMsg.messageId);

      if (lastMessageMsg.index === -1 && lastMessageMsg.type === SessionMessageType.msg) {
        setAnswering(false);
        dispatchEvent(
          new CustomEvent(`${EVENT.UPDATE_CHAT_NOTE}`, {
            detail: lastMessageMsg.sessionId
          })
        );
        dispatchEvent(
          new CustomEvent(CONTAINER_EVENT.ANSWERED, { detail: lastMessageMsg.sessionId })
        );

        setConnectedSessionIdList((prev) => {
          const list = prev ?? []; // 保证 prev 不为 undefined
          const newSet = new Set(list);

          newSet.add(lastMessageMsg.sessionId);

          return Array.from(newSet);
        });

        dispatchEvent(new Event(CONTAINER_EVENT.UPDATE_SUBSCRIPTION));

        if (endingMsgTime) {
          const endTime = Date.now();
          const duration = endTime - endingMsgTime;

          reportOther(EReportOtherKey.timer_receive_ending_duration, {
            key1: mindId,
            key2: duration,
            key3: localStorage.getItem(EGlobalLocalStorageKey.userId) || '',
            key4: sessionId || '',
            key5: lastMsg?.messageId,
            key6: endingMsgTime,
            key7: endTime
          });

          endingMsgTime = undefined;
        }

        if (sendMsgTime) {
          const endTime = Date.now();
          const duration = endTime - sendMsgTime;

          reportOther(EReportOtherKey.timer_receive_final_message, {
            key1: mindId,
            key2: duration,
            key3: localStorage.getItem(EGlobalLocalStorageKey.userId) || '',
            key4: sessionId || '',
            key5: lastMsg?.messageId,
            key6: sendMsgTime,
            key7: endTime
          });

          sendMsgTime = undefined;
        }
      }
    }

    if (receivedMsgStartTime && lastMessageMsg?.type === SessionMessageType.msg) {
      const endTime = Date.now();
      const duration = endTime - receivedMsgStartTime;

      reportOther(EReportOtherKey.timer_receive_message, {
        key1: mindId,
        key2: duration,
        key3: localStorage.getItem(EGlobalLocalStorageKey.userId) || '',
        key4: sessionId || '',
        key5: lastMsg?.messageId,
        key6: receivedMsgStartTime,
        key7: endTime
      });

      receivedMsgStartTime = undefined;
    }
  });

  const { msgList, appendReceivedMsg, clearMsgList, appendUnShowedMsgId } = useSessionMsgList({
    messageList,
    receivedNewMsgCallback
  });
  const [receivedAction, setAction] = useState<SessionAction>();
  const onMsgUpdateListener = useMemoizedFn((newMsgList: SessionMessageModel[]) => {
    setAction(undefined);

    // 处理客户端发送的消息
    if (
      newMsgList.at(-1)?.sender === SENDER_TYPE.client &&
      sessionInfo &&
      (!sessionInfo.type ||
        ![ESessionType.GROUP, ESessionType.OTHERBOT, ESessionType.FRIEND].includes(
          sessionInfo.type
        ))
    ) {
      setLoadingMsgId(newMsgList.at(-1)?.messageId);
    }

    appendReceivedMsg(newMsgList);
  });
  const onErrorListener = useMemoizedFn((errorMsg: ErrorMsg) => {
    const { messageId, subCode, subCodeReason, subCodeActions, seqId, sendUserId } =
      errorMsg as ErrorMsg & { sendUserId?: string };

    if (sendUserId && sendUserId !== userProfile?.userId) {
      return;
    }

    lastErrorSeqIdRef.current = seqId;

    reportOther(EReportOtherKey.chat_receive_sub_code, {
      key1: mindId,
      key2: localStorage.getItem(EGlobalLocalStorageKey.userId) || '',
      key3: sessionId || '',
      key4: subCode
    });

    setLoadingMsgId(undefined);
    subCodeActions?.forEach((subCodeAction) => {
      switch (subCodeAction) {
        case ChatSessionSubCodeAction.RESTART_SESSION:
          setAction({
            type: SessionActionType.ERROR,
            content: subCodeReason ?? "Something's wrong. Please try again."
          });

          if (messageId) {
            appendUnShowedMsgId({ messageId, type: MessageItemType.umm });
          }

          break;
        case ChatSessionSubCodeAction.RESEND_MSG:
          setAction({
            type: SessionActionType.WARNING,
            content: subCodeReason ?? 'Response timeout. Please try again to send a query.'
          });

          if (messageId) {
            appendUnShowedMsgId({ messageId, type: MessageItemType.umm });
          }

          break;
        case ChatSessionSubCodeAction.SIMPLE_HINT:
          setAction({
            type: SessionActionType.ERROR_WITHOUT_ACTION,
            content: subCodeReason ?? 'Something went wrong. Please try again.'
          });

          if (messageId) {
            appendUnShowedMsgId({ messageId, type: MessageItemType.umm });
          }

          break;
        case ChatSessionSubCodeAction.OPEN_PAGE:
          if (
            [SessionErrorCode.FREE_USED_UP_BYOK_PLAN, MV_SESSION_ERROR_CODE.NO_ENOUGH_KEY].includes(
              subCode
            )
          ) {
            dispatchEvent(new CustomEvent(CONTAINER_EVENT.TRIAL_OVER));
          }

          break;
      }
    });
  });
  const onFetchSessionHistoryListener = (res: HistoryMessageModel) => {
    setNoMore(res.noMore);
    setSessionInfo(res.sessionInfo);
  };

  const onMsgUpdateListenerRef = useRef(throttle(onMsgUpdateListener, 200));
  const onErrorListenerRef = useRef(onErrorListener);
  const onOpenSessionListenerRef = useRef(onFetchSessionHistoryListener);

  const createSession = useMemoizedFn((createProps: ICreateProps) => {
    const { newSession, originSessionId, cb, extParams = {} } = createProps;

    if (disabled) {
      return Promise.reject('Oops');
    }

    if (!privateMindConfig || !sessionManager) {
      return Promise.reject('mindConfig or sessionManager is undefined');
    }

    const sourceMessageId =
      new URLSearchParams(location.search).get('sourceMessageId') || undefined;

    if (sourceMessageId) {
      updateURLSearch({ sourceMessageId: undefined });
    }

    setIsCreatingSession(true);
    clearMsgList(); // 现在对话都以session为维度，开启session的时候默认自动把老消息清空

    // 创建session 关闭启动页的消息, 关闭老的session
    dispatchEvent(new Event(CONTAINER_EVENT.CLOSE_ONBOARD));
    dispatchEvent(new Event(CONTAINER_EVENT.CLOSE_SESSION));
    // 让ChatList先不显示，等到msgList来的时候再展示
    setReady(false);

    return sessionManager
      .createSession({
        ...privateMindConfig,
        mode: privateMindConfig.mode ?? MIND_MODE.PERSONAL,
        openNewSession: newSession,
        originSessionId,
        callback: {
          onMsgUpdateListener: onMsgUpdateListenerRef.current,
          onErrorListener: onErrorListenerRef.current,
          onFetchSessionHistoryListener: onOpenSessionListenerRef.current
        },
        extParams
      })
      .then((sessionDataInfo) => {
        console.log('123123123 create then', sessionDataInfo);
        const { sessionId: newSessionId, sessionInfo: newSessionInfo } = sessionDataInfo;

        setSessionInfo(newSessionInfo);

        setStorageSession(newSessionId);

        setIsCreatingSession(false);

        sessionManager.startReceiveMsg(newSessionId);

        cb?.(newSessionId);

        return sessionDataInfo;
      })
      .catch((e) => {
        console.log('123123123 create catch', e);
      });
  });

  const doCreateSession = (params: ICreateProps = {}): Promise<any> => {
    if (disabled) {
      return Promise.reject('Oops');
    }

    if (inSharedHistory) {
      return Promise.reject('inSharedHistory');
    }

    const startSessionTime = Date.now();

    console.log('123123123 doCreate', params);

    return createSession(params).then((sessionDataInfo) => {
      const endTime = Date.now();
      const duration = endTime - startSessionTime;

      reportOther(EReportOtherKey.timer_open_session, {
        key1: mindId,
        key2: duration,
        key3: localStorage.getItem(EGlobalLocalStorageKey.userId) || '',
        key4: sessionDataInfo?.openSessionTime || undefined,
        key5: sessionDataInfo?.openSessionCount || undefined,
        key6: sessionDataInfo?.confirmSessionTime || undefined,
        key7: getGMTNumber(),
        key8: startSessionTime,
        key9: endTime
      });

      return sessionDataInfo;
    });
  };

  useEffect(() => {
    function _createSession(e: Event) {
      if (disabled) {
        return;
      }

      const data = (e as CustomEvent).detail as EventCreateSessionInterface;
      const { params, callback } = data;

      const _isIceBreaking = params.extParams?.isIceBreaking;

      console.log('123123123 create event detail', data.params);

      doCreateSession(params).then((sessionData) => {
        const { sessionId: sid } = sessionData;

        if (sid) {
          callback?.(sid);
          setIceBreaking(!!_isIceBreaking);
        } else {
          console.error('No SessionId to send msg');
        }
      });
    }

    if (!disabled) {
      console.log('123123123 [useChatSession] register', new Date().toISOString());
      addEventListener(CONTAINER_EVENT.CREATE_SESSION, _createSession);
    }

    return () => {
      console.log('123123123 [useChatSession] unregister', new Date().toISOString());
      removeEventListener(CONTAINER_EVENT.CREATE_SESSION, _createSession);
    };
  }, [disabled]);

  const sendMsg = useMemoizedFn(
    (
      msgData: SendMsgData,
      params?: { sessionId?: string; voiceChannel?: boolean }
    ): undefined | Promise<boolean> => {
      const sid = params?.sessionId || sessionId;

      if (disabled) {
        return;
      }

      console.log('123123123 send in useChatSession', sid, sessionManager);

      if (!sid || !sessionManager) {
        return;
      }

      // checkHomepage
      if (homepageData && userProfile && (!userProfile.homepage?.cover || !userProfile.aboutMe)) {
        modal.open({
          content: t('login.completeYourSecondMeToContinue'),
          footer: (
            <div
              className={
                'w-full max-w-[270px] rounded-[24px] bg-primary h-12 flex items-center text-primary-white text-[18px] leading-8 font-medium cursor-pointer transition-colors hover:bg-primary-sub-color'
              }
              onClick={() => {
                dispatchEvent(
                  new CustomEvent(EVENT.HOMEPAGE_SHOW_LOGIN_MODAL, {
                    detail: { open: true, type: 'initPA' }
                  })
                );
                modal.close();
              }}
            >
              <div className="w-full text-center">{t('login.continueToComplete')}</div>
            </div>
          )
        });

        return;
      }

      const { value, files, quote } = msgData;

      const startTime = Date.now();

      receivedHintMsgStartTime = startTime;
      receivedMsgStartTime = startTime;
      sendMsgTime = startTime;

      setSendMsgLoading(true);
      setShowMessageLoading(true);

      return sessionManager
        .sendMessage({
          sessionId: sid,
          msg: {
            data: {
              content: value,
              urls: files,
              quote
            },
            dataType: WS_MSG_DATA_TYPE.text,
            voiceChannel: params?.voiceChannel ?? false
          }
        })
        .then(() => {
          const endTime = Date.now();
          const duration = endTime - startTime;

          dispatchEvent(new CustomEvent(EVENT.ADD_VOICE_SESSION_ID, { detail: sid }));

          reportOther(EReportOtherKey.timer_send_msg, {
            key1: mindId,
            key2: duration,
            key3: localStorage.getItem(EGlobalLocalStorageKey.userId) || '',
            key4: sid
          });
          dispatchEvent(new Event(EVENT.HAS_SEND_MESSAGE));
          sendMsgCb?.();

          return true;
        })
        .catch((e) => {
          console.error('sendMsg===>', e);
          let failMessage = 'Failed to send. Please try again.';

          try {
            const jsonParse = JSON.parse(e.message);

            failMessage = jsonParse.message;

            if (jsonParse?.subCode === 'anonymous.message.limit') {
              setInputShowLogin(true);
              failMessage = '';
              modal.open({
                content: t('login.createYourSecondMeToContinue'),
                footer: (
                  <div
                    className={
                      'w-full max-w-[270px] rounded-[24px] bg-primary h-12 flex items-center text-primary-white text-[18px] leading-8 font-medium cursor-pointer transition-colors hover:bg-primary-sub-color'
                    }
                    onClick={() => {
                      dispatchEvent(
                        new CustomEvent(EVENT.HOMEPAGE_SHOW_LOGIN_MODAL, {
                          detail: { open: true }
                        })
                      );
                      modal.close();
                    }}
                  >
                    <div className="w-full text-center">{t('login.goLogin')}</div>
                  </div>
                )
              });
            }
          } catch {}

          setShowMessageLoading(false);

          failMessage ? message.error(failMessage) : null;

          return false;
        })
        .finally(() => {
          setSendMsgLoading(false);
        });
    }
  );

  const sendAction = () => {
    setLoadingMsgId(undefined);
    const now = Date.now();

    receivedHintMsgStartTime = now;
    receivedMsgStartTime = now;
    sendMsgTime = now;
  };

  const closeSession = useMemoizedFn((closeSessionId: string) => {
    sessionManager?.closeSession(closeSessionId);
    dispatchEvent(new CustomEvent(EVENT.REMOVE_VOICE_SESSION_ID, { detail: closeSessionId }));
    updateSummary({ sessionId: closeSessionId });
    setStorageSession('');
    setSessionInfo(undefined);
    setNoMore(undefined);
  });

  const reOpenSession = useMemoizedFn((openSessionParam?: IOpenSessionParam) => {
    if (disabled) {
      return;
    }

    if (isCreatingSession) {
      showToast('Session connecting, please try again later.');

      return;
    }

    if (sessionId) {
      setLoadingMsgId(undefined);
    }

    const now = Date.now();

    createSession({ ...openSessionParam, newSession: true }).then(() => {
      const endTime = Date.now();
      const duration = endTime - now;

      reportOther(EReportOtherKey.restart_open_session, {
        key1: privateMindConfig.mindId,
        key2: duration,
        key3: localStorage.getItem(EGlobalLocalStorageKey.userId) || '',
        key4: undefined,
        key5: undefined,
        key6: undefined,
        key7: getGMTNumber(),
        key8: now,
        key9: endTime
      });
    });
  });

  const continueSession = useMemoizedFn((sessionDetail: SessionSourceInterface) => {
    if (disabled) {
      return;
    }

    if (isCreatingSession) {
      showToast('Session connecting, please try again later.');

      return;
    }

    const { sessionId: originSessionId } = sessionDetail;

    // 启动页下是没有session
    if (sessionId) {
      setLoadingMsgId(undefined);
      setSessionInfo(undefined);
    }

    const now = Date.now();

    createSession({ originSessionId: originSessionId })
      .then(() => {
        const endTime = Date.now();
        const duration = endTime - now;

        reportOther(EReportOtherKey.continue_open_session, {
          key1: privateMindConfig.mindId,
          key2: duration,
          key3: localStorage.getItem(EGlobalLocalStorageKey.userId) || '',
          key4: undefined,
          key5: undefined,
          key6: undefined,
          key7: getGMTNumber(),
          key8: now,
          key9: endTime
        });
      })
      .catch((e) => {
        console.error(e, 'Continue session failed');
      });
  });

  useEffect(() => {
    function _continueSession(e: Event) {
      if (disabled) {
        return;
      }

      const sessionDetail = (e as CustomEvent).detail as SessionSourceInterface | undefined;

      dispatchEvent(new Event(CONTAINER_EVENT.CLOSE_ONBOARD));

      if (sessionDetail) {
        continueSession(sessionDetail);
      }
    }

    if (!disabled) {
      window.addEventListener(CONTAINER_EVENT.OPEN_SESSION_WITH_SOURCE, _continueSession);
    }

    return () => {
      window.removeEventListener(CONTAINER_EVENT.OPEN_SESSION_WITH_SOURCE, _continueSession);
    };
  }, [continueSession, disabled]);

  useEffect(() => {
    function _reOpenSession(e: Event) {
      reOpenSession((e as CustomEvent).detail);
    }

    function _closeSession() {
      if (sessionId) {
        closeSession(sessionId);

        setLoadingMsgId(undefined);
      } else {
        console.log('sessionRef.current is undefined');
      }
    }
    window.addEventListener(CONTAINER_EVENT.CLOSE_SESSION, _closeSession);
    window.addEventListener(CONTAINER_EVENT.RESTART_SESSION, _reOpenSession);

    return () => {
      window.removeEventListener(CONTAINER_EVENT.CLOSE_SESSION, _closeSession);
      window.removeEventListener(CONTAINER_EVENT.RESTART_SESSION, _reOpenSession);
    };
  }, [closeSession, reOpenSession, sessionId]);

  useEffect(() => {
    const handleLoading = () => sendAction();

    window.addEventListener(CONTAINER_EVENT.SHORTCUT_LOADING, handleLoading);

    return () => {
      window.removeEventListener(CONTAINER_EVENT.SHORTCUT_LOADING, handleLoading);
    };
  }, []);

  useEffect(() => {
    const resendMsg = () => {
      let lastMsg: MessageModel | undefined;

      for (let index = msgList.length - 1; index >= 0; index--) {
        const element = msgList[index];

        if (element.type === MessageItemType.client) {
          lastMsg = element;

          break;
        }
      }

      const lastSendMsg: MessageItem | undefined = lastMsg?.list.at(-1) as MessageItem | undefined;

      if (!lastSendMsg) {
        return;
      }

      sendMsg({
        value: lastSendMsg.data.content,
        files: lastSendMsg.data.urls,
        quote: lastSendMsg.data.quote
      });
    };

    addEventListener(INNER_EVENT.RESEND_MSG, resendMsg);

    return () => {
      removeEventListener(INNER_EVENT.RESEND_MSG, resendMsg);
    };
  }, [msgList, sendMsg]);

  const fetchHistory = useMemoizedFn(() => {
    if (sessionId && sessionManager) {
      let seqId: string | undefined = undefined;

      // 找到第一个不是 系统消息的 msgId
      for (let index = 0; index < msgList.length; index++) {
        const msg = msgList[index];

        for (let item = 0; item < msg.list.length; item++) {
          const msgItem = msg.list[item];

          if (msgItem.sender !== MessageItemType.system) {
            seqId = msgItem.seqId;

            break;
          }
        }

        if (seqId) {
          break;
        }
      }

      return sessionManager.fetchSessionHistory(sessionId, seqId);
    }

    return Promise.resolve({ msgList: [], noMore: true, sessionInfo: null });
  });

  useEffect(() => {
    dispatchEvent(
      new CustomEvent(INNER_EVENT.SCROLL_TO_BOTTOM, { detail: { msgType: msgList.at(-1)?.type } })
    );
  }, [msgList]);

  useEffect(() => {
    if (msgList.length && msgList.every((i) => i.messageId === msgList[0]?.messageId)) {
      setGreetingOnly(true);
    } else {
      setGreetingOnly(false);
    }
  }, [msgList]);

  return {
    msgList,
    noMore,
    sessionId,
    createSession,
    doCreateSession,
    sendMsg,
    sendAction,
    receivedAction,
    loadingMsgId,
    fetchHistory,
    isCreatingSession
  };
}
